import React, { useEffect, useState, useCallback, useRef } from "react";
import {
    Avatar,
    Conversation,
    ConversationList,
} from "@chatscope/chat-ui-kit-react";
import InitialsAvatar from "react-avatar";
import {
    collection,
    getDocs,
    onSnapshot,
    orderBy,
    query,
    limit,
    startAfter,
    DocumentData,
    QueryDocumentSnapshot,
} from "firebase/firestore";
import InfiniteScroll from "react-infinite-scroll-component";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { ChatRoom, EntityChatRoom } from "../types/chat";
import { useFirebase } from "../contexts/FirebaseContext";
import { AppDispatch, RootState } from "../redux-stores/store";
import { setBGWhite } from "../redux-stores/slices/layoutSlice";
import PerfectScrollbar from 'react-perfect-scrollbar'
import DOMPurify from "dompurify";
const PAGE_SIZE = 20;

// ---------------- Custom Conversation ----------------
const CustomConversation: React.FC<{ chatRoom: EntityChatRoom, active: boolean }> = ({ chatRoom, active }) => {
    const { auth } = useFirebase();
    const lastMessage = chatRoom.lastMessage;
    const currentUserId = auth.currentUser?.uid;

    return (
        <Conversation
            info={DOMPurify.sanitize((lastMessage?.text?.split('&nbsp;')?.[0] ?? '') || "No messages yet", {ALLOWED_TAGS: [], ALLOWED_ATTR: []})} 
            lastSenderName={
                currentUserId === lastMessage?.senderId
                    ? "You"
                    : lastMessage?.senderName || ""
            }
            name={`${chatRoom.campaign.name}${chatRoom.campaign.brand?.name ? ` - ${chatRoom.campaign.brand.name}` : ''}`}
            active={active}
            unreadCnt={chatRoom?.unreadCount ?? 0}
            style={{ alignItems: 'center', gap: '0.5rem' }}
        >
            <Avatar className="m-0">
                <InitialsAvatar
                    name={chatRoom.campaign?.name ?? ""}
                    maxInitials={1}
                    size="40"
                    round
                />
            </Avatar>

        </Conversation>
    );
};

// ---------------- Chat Layout ----------------
const ChatLayout: React.FC<{ children: React.ReactElement, setEntityChatRoom?: (room: EntityChatRoom | null) => void, setListLoaded?: (loaded: boolean) => void }> = ({ children, setEntityChatRoom, setListLoaded }) => {
    const { uuid: activeRoomId } = useParams();
    const [chatRooms, setChatRooms] = useState<ChatRoom[]>([]);
    // const [lastVisible, setLastVisible] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
    const [hasMore, setHasMore] = useState(true);
    const { db, auth, firebaseUser } = useFirebase();
    const navigate = useNavigate();
    const influencerId = useSelector((state: RootState) => state.influencer.influencer?.uuid);
    const dispatch: AppDispatch = useDispatch();
    const lastVisibleRef = useRef<QueryDocumentSnapshot<DocumentData> | null>(null);

    useEffect(() => {
        dispatch(setBGWhite(true));

        return () => {
            dispatch(setBGWhite(false))
        }
    }, [])


    // Fetch first page with real-time updates
    useEffect(() => {

        if (!influencerId || !firebaseUser) return;

        const q = query(
            collection(db, "entityChatRooms", influencerId, 'rooms'),
            orderBy("lastMessage.timestamp", "desc"),
            limit(PAGE_SIZE)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            if (!snapshot.empty) {
                const docs = snapshot.docs.map(d => ({
                    ...(d.data() as ChatRoom),
                    uuid: d.id
                }));
                setChatRooms(docs);
                lastVisibleRef.current = snapshot.docs[snapshot.docs.length - 1];
                setHasMore(snapshot.docs.length === PAGE_SIZE);
            } else {
                setChatRooms([]);
                setHasMore(false);
            }
            setListLoaded?.(true);
        });

        return () => unsubscribe();
    }, [influencerId, firebaseUser]);

    // Fetch next page (non-realtime)
    const fetchNextPage = useCallback(async () => {
        const lastVisible = lastVisibleRef.current;
        if (!lastVisible || !db || !influencerId) return;

        const q = query(
            collection(db, "entityChatRooms", influencerId, 'rooms'),
            orderBy("lastMessage.timestamp", "desc"),
            startAfter(lastVisible),
            limit(PAGE_SIZE)
        );

        const snapshot = await getDocs(q);
        if (!snapshot.empty) {
            const docs = snapshot.docs.map(d => ({
                ...(d.data() as ChatRoom),
                uuid: d.id
            }));
            setChatRooms(prev => [...prev, ...docs]);
            lastVisibleRef.current = snapshot.docs[snapshot.docs.length - 1];
            setHasMore(snapshot.docs.length === PAGE_SIZE);
        } else {
            setHasMore(false);
        }
    }, [db, influencerId]);


    useEffect(() => {
        if (activeRoomId && chatRooms && setEntityChatRoom) {
            const activeChatRoom = chatRooms.find((room) => room.uuid === activeRoomId);
            setEntityChatRoom(activeChatRoom ?? null)
        } else {
            setEntityChatRoom?.(null);
        }
    }, [activeRoomId, chatRooms, setEntityChatRoom])

    return (
        <div className="container-fluid h-100">
            <div className="row h-100 mb-0">
                {/* Conversation List */}
                <div className={`col-12 border-end p-0 ${(activeRoomId) ? 'd-none' : 'd-flex'} flex-column chat-list-container`}>
                    <PerfectScrollbar id="scrollableDiv">
                        <InfiniteScroll
                            dataLength={chatRooms.length}
                            next={fetchNextPage}
                            className="row g-3 overflow-x-hidden m-0"
                            hasMore={hasMore}
                            scrollableTarget="scrollableDiv"
                            loader={<></>}
                        >
                            {chatRooms.map((chatRoom, i) => (
                                <div
                                    className={`m-0 p-0 border-top ${(i + 1) === chatRooms.length ? 'border-bottom' : ''}`}
                                    key={chatRoom.uuid + i}
                                    onClick={() => navigate(`/chat/${chatRoom.uuid}`)}
                                >
                                    <CustomConversation chatRoom={chatRoom} active={activeRoomId === chatRoom.uuid} />
                                </div>
                            ))}
                            {
                                (chatRooms.length === 0) &&
                                <div className="d-flex w-100 chat-list justify-content-center align-items-center">
                                    <div className="length-zero m-0">
                                        <h3>No recent chats!</h3>
                                    </div>
                                </div>
                            }
                        </InfiniteScroll>
                    </PerfectScrollbar>
                </div>

                {/* Chat Window */}
                <div className={`col-12 ${!(activeRoomId) ? 'd-none' : 'd-flex'} flex-column p-0 $`}>
                    <div className="flex-grow-1 overflow-auto chat-window">
                        {children}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ChatLayout;
